import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmpresaAdmin } from '@apen/shared';
import { api } from '../lib/api';
import { sessionKey } from './useSession';

/** Hooks da empresa ativa e da tela de empresas (admin geral). */

export interface ContaParaEmpresa {
  id: number;
  name: string;
  email: string;
  role: string;
  admin_geral: boolean;
}

/**
 * Troca a empresa ativa.
 *
 * Todo dado em cache e da empresa anterior. Invalidar so marcaria como velho e
 * a tela mostraria os numeros da outra empresa ate o refetch chegar; remover o
 * cache tambem nao basta, porque a tela aberta segura o resultado que ja tinha.
 * O reset volta cada consulta ao estado inicial (a tela mostra o esqueleto) e
 * busca de novo as que estao em uso.
 */
export function useTrocarEmpresa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (empresaId: number) =>
      api.put<{ empresa_id: number }>('/api/me/empresa', { empresa_id: empresaId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionKey });
      await queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== sessionKey[0] });
    },
  });
}

/**
 * Troca o banco de trabalho. Mesmo cuidado da troca de empresa: o cache
 * inteiro era do banco anterior (extratos, lancamentos, travas).
 */
export function useTrocarBanco() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bankId: number) => api.put<{ bank_id: number }>('/api/me/banco', { bank_id: bankId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionKey });
      await queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== sessionKey[0] });
    },
  });
}

export function useEmpresasAdmin() {
  return useQuery({
    queryKey: ['empresas', 'admin'],
    queryFn: () => api.get<EmpresaAdmin[]>('/api/admin/empresas'),
  });
}

export function useContasParaEmpresa() {
  return useQuery({
    queryKey: ['empresas', 'contas'],
    queryFn: () => api.get<ContaParaEmpresa[]>('/api/admin/empresas/contas'),
  });
}

function useInvalidarEmpresas() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['empresas'] });
    void queryClient.invalidateQueries({ queryKey: sessionKey });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };
}

export function useCriarEmpresa() {
  const invalidar = useInvalidarEmpresas();
  return useMutation({
    mutationFn: (body: { nome: string; cnpj: string | null }) =>
      api.post<{ id: number }>('/api/admin/empresas', body),
    onSuccess: invalidar,
  });
}

export function useAtualizarEmpresa() {
  const invalidar = useInvalidarEmpresas();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; nome: string; cnpj: string | null; is_active: boolean }) =>
      api.put<{ id: number }>(`/api/admin/empresas/${String(id)}`, body),
    onSuccess: invalidar,
  });
}

export function useDefinirUsuariosDaEmpresa() {
  const invalidar = useInvalidarEmpresas();
  return useMutation({
    mutationFn: ({ id, user_ids }: { id: number; user_ids: number[] }) =>
      api.put<{ entraram: number; sairam: number }>(
        `/api/admin/empresas/${String(id)}/usuarios`,
        { user_ids },
      ),
    onSuccess: invalidar,
  });
}
